import { ReactElement, memo } from 'react';
import { Button, IButtonProps, ICommonButtonProps } from '../../components/Button/Button';
import { IIconProps, Icon } from '../../components/Icon/Icon';
import { ILinkButtonProps, LinkButton } from '../../components/LinkButton/LinkButton';
import { IStatusIconProps, StatusIcon } from '../../components/StatusIcon/StatusIcon';
import React from 'react';
import { SIZES } from '../../fundamentals/sizes/sizes';
import { Heading5, Heading6, IHeadingEoraProps } from '../../components/Headings/Headings';
import { IGutterProps } from '../../fundamentals/spaces/space';
import { IBadgeProps } from '../../components/Badge/Badge';
import { IAlertState } from '../../hooks/useAlertState/useAlertState';
import { useTestId } from '../../hooks/useTestId/useTestId';

export interface IModalTestId {
  testId?: string;
}

export interface IModalIcon extends IModalTestId, Omit<IIconProps, 'size'> {
  kind: 'Icon';
}

export interface IModalStatusIcon extends IModalTestId, Omit<IStatusIconProps, 'size'> {
  kind: 'StatusIcon';
}

export interface IModalButton extends IModalTestId, Omit<ICommonButtonProps, 'size'> {
  kind: 'Button';
}

export interface IModalLinkButton extends IModalTestId, Omit<ILinkButtonProps, 'size'> {
  kind: 'LinkButton';
}

export interface IModalBadge extends IModalTestId, Pick<IBadgeProps, 'prefix' | 'label' | 'variant' | 'light'> {}

export interface IModalAlert extends IModalTestId, IAlertState {}

const isModalStatusIcon = (icon: IModalStatusIcon | IModalIcon): icon is IModalStatusIcon => (icon as IModalStatusIcon).kind === 'StatusIcon';
const isModalLinkButton = (button: IModalButton | IModalLinkButton): button is IModalLinkButton => (button as IModalLinkButton).kind === 'LinkButton';

const HeaderIcon: React.FC<{ isMobile: boolean; preTitle?: ReactElement<IIconProps | IStatusIconProps>; icon?: IModalIcon | IModalStatusIcon }> = ({
  isMobile,
  preTitle,
  icon,
  ...rest
}) => {
  const { generateTestId } = useTestId(rest, 'HeaderIcon');
  if (!icon) return preTitle;
  return isModalStatusIcon(icon) ? (
    <StatusIcon size={isMobile ? SIZES.X_SMALL : SIZES.SMALL} {...icon} {...generateTestId(icon.testId ?? 'StatusIcon')} />
  ) : (
    <Icon size={isMobile ? SIZES.X_SMALL : SIZES.SMALL} {...icon} {...generateTestId(icon.testId ?? 'Icon')} />
  );
};

export const ModalHeaderIcon = memo(HeaderIcon);

const FooterButtons: React.FC<{
  footerButtons?: (IModalButton | IModalLinkButton)[];
  ctaButtons?: ReactElement<IButtonProps> | ReactElement<IButtonProps>[];
}> = ({ footerButtons, ctaButtons, ...rest }) => {
  const { generateTestId } = useTestId(rest, 'FooterButtons');
  if (!footerButtons) return ctaButtons;
  return footerButtons.map((buttonProps, key) =>
    isModalLinkButton(buttonProps) ? (
      <LinkButton key={key} size={'small'} {...buttonProps} {...generateTestId(buttonProps.testId ?? 'LinkButton')} />
    ) : (
      <Button key={key} size={'small'} {...buttonProps} {...generateTestId(buttonProps.testId ?? 'Button')} />
    )
  );
};

export const ModalFooterButtons = memo(FooterButtons);

const HeadingProps: IHeadingEoraProps & IGutterProps = {
  gutterBottom: false,
  headingType: 'SEMI_BOLD',
  lineHeight: 24
};

const Title: React.FC<{ isMobile: boolean; title?: string }> = ({ isMobile, title }) =>
  isMobile ? <Heading6 {...HeadingProps}>{title}</Heading6> : <Heading5 {...HeadingProps}>{title}</Heading5>;

export const ModalTitle = memo(Title);
